import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  FormControl,
  Flex,
  SimpleGrid,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Icon,
  Stack,
  FormLabel,
  Input,
  IconButton,
  Select,
} from "@chakra-ui/react";
import {
  FaEdit,
  FaUser,
  FaMoneyBillAlt,
  FaEnvelope,
  FaHome,
  FaCalendarAlt,
  FaVenusMars,
  FaBook,
  FaTint,
  FaIdCard,
  FaMale,
  FaFemale,
  FaPhone,
} from "react-icons/fa";
import { EditIcon } from "@chakra-ui/icons";
import axios from "axios";
import { BASE_URL } from "../../../../../Globalapi/Globalapi";

const EditBookModal_Trs = ({ issueId, handleSaveEdit }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState({
    name: "",
    issueDate: "",
    status: "",
    returnDate: "",
    fineAmount: "",
    teacherName: "",
    studentName: "", // This field will hold the student name
    dueDate: "", // Added dueDate to state
  });

  useEffect(() => {
    if (issueId) {
      fetchBookIssue(issueId);
    }
  }, [issueId]);

  const fetchBookIssue = async (issueId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/admin/library/issues/view_issue.php?issue_id=${issueId}`
      );
      console.log(response);

      if (response.data.success) {
        // Map the API response to the selectedBook state
        setSelectedBook({
          name: response.data.data.title, // Map title to name
          issueDate: response.data.data.issue_date || "",
          status: response.data.data.status || "",
          returnDate: response.data.data.return_date || "",
          fineAmount: response.data.data.is_fine ? "Yes" : "No", // Adjust fine amount if needed
          teacherName: response.data.data.member_name || "", // Map member_name to teacherName
          dueDate: response.data.data.due_date || "", // Set due date here
        });
      } else {
        console.error(response.data.message);
      }
    } catch (error) {
      console.error("Error fetching book issue:", error);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSelectedBook((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveChanges = async () => {
    try {
      // Prepare the data to be sent in the PUT request
      const updatedData = {
        user_id: sessionStorage.getItem("user_id"),
        issue_id: issueId, // The issue ID being updated
        due_date: selectedBook.dueDate, // Send dueDate as part of the request
        fine_amount: selectedBook.fineAmount, // Fine amount
      };
      console.log("up", updatedData);

      // Send the PUT request to update the book issue
      const response = await axios.put(
        `${BASE_URL}/admin/library/issues/edit_issue.php`,
        updatedData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        // Handle successful save (you can call handleSaveEdit or other actions)
        handleCloseModal();
      } else {
        console.error("Failed to save changes:", response.data.message);
      }
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };

  return (
    <div>
      {/* Icon Button */}
      <IconButton
        icon={<EditIcon />}
        colorScheme="blue"
        size="sm"
        onClick={handleOpenModal}
      />

      {/* Modal */}
      <Modal
        isCentered
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaEdit} boxSize={5} mr={2} />
            Edit Teacher Book Details
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            <Flex direction="column">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {/* Teacher Name */}
                <FormControl id="teacherName" isRequired>
                  <FormLabel>
                    <Icon as={FaUser} mr={1} color="teal.500" /> Teacher Name
                  </FormLabel>
                  <Input
                    name="teacherName"
                    value={selectedBook.teacherName}
                    isReadOnly
                    placeholder="Teacher Name"
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px teal.500",
                    }}
                  />
                </FormControl>

                {/* Book Name */}
                <FormControl id="name" isRequired>
                  <FormLabel>
                    <Icon as={FaBook} mr={1} color="teal.500" /> Book Name
                  </FormLabel>
                  <Input
                    name="name"
                    value={selectedBook.name}
                    isReadOnly
                    placeholder="Book Name"
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px teal.500",
                    }}
                  />
                </FormControl>

                {/* Issue Date */}
                <FormControl id="issueDate" isRequired>
                  <FormLabel>
                    <Icon as={FaCalendarAlt} mr={1} color="teal.500" /> Issue
                    Date
                  </FormLabel>
                  <Input
                    type="date"
                    name="issueDate"
                    value={selectedBook.issueDate}
                    onChange={handleChange}
                    placeholder="Select Issue Date"
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px teal.500",
                    }}
                  />
                </FormControl>

                {/* Status */}
                <FormControl id="status" isRequired>
                  <FormLabel>
                    <Icon as={FaMoneyBillAlt} mr={1} color="teal.500" /> Status
                  </FormLabel>
                  <Select
                    name="status"
                    value={selectedBook.status}
                    onChange={handleChange}
                    placeholder="Select Status"
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px teal.500",
                    }}
                  >
                    <option value="Issued">Issued</option>
                    <option value="Returned">Returned</option>
                    <option value="Overdue">Overdue</option>
                  </Select>
                </FormControl>

                {/* Return Date */}
                <FormControl id="returnDate" isRequired>
                  <FormLabel>
                    <Icon as={FaCalendarAlt} mr={1} color="teal.500" /> Return
                    Date
                  </FormLabel>
                  <Input
                    type="date"
                    name="returnDate"
                    value={selectedBook.returnDate}
                    onChange={handleChange}
                    placeholder="Select Return Date"
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px teal.500",
                    }}
                  />
                </FormControl>

                {/* Due Date */}
                <FormControl id="dueDate" isRequired>
                  <FormLabel>
                    <Icon as={FaCalendarAlt} mr={1} color="teal.500" /> Due Date
                  </FormLabel>
                  <Input
                    type="date"
                    name="dueDate"
                    value={selectedBook.dueDate}
                    onChange={handleChange}
                    placeholder="Select Due Date"
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px teal.500",
                    }}
                  />
                </FormControl>

                {/* Fine Amount */}
                <FormControl id="fineAmount" isRequired>
                  <FormLabel>
                    <Icon as={FaMoneyBillAlt} mr={1} color="teal.500" /> Fine
                    Amount
                  </FormLabel>
                  <Input
                    name="fineAmount"
                    value={selectedBook.fineAmount}
                    onChange={handleChange}
                    placeholder="Enter Fine Amount"
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px teal.500",
                    }}
                  />
                </FormControl>
              </SimpleGrid>
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              onClick={handleSaveChanges}
            >
              Save
            </Button>
            {/* <Button onClick={handleCloseModal} ml={3}>
              Cancel
            </Button> */}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default EditBookModal_Trs;
